'use client';

import type { ComponentProps, Ref } from 'react';

import * as CheckboxPrimitive from '@radix-ui/react-checkbox';

import { Icon, Icons } from '../icon';
import { cn } from '../lib/utils';

type Size = 'sm' | 'md';

const sizeStyles: Record<Size, string> = {
  sm: 'size-4',
  md: 'size-5',
};

const indicatorIconSize: Record<Size, string> = {
  sm: 'text-[10px]',
  md: 'text-s',
};

type Props = Omit<ComponentProps<typeof CheckboxPrimitive.Root>, 'size'> & {
  size?: Size;
  ref?: Ref<HTMLButtonElement>;
};

export const Checkbox = ({ size = 'sm', className, ref, ...props }: Props) => {
  return (
    <CheckboxPrimitive.Root
      ref={ref}
      data-slot="checkbox"
      className={cn(
        // bg-transparent avoids inheriting sibling `bg-white` row-highlight overrides.
        'shrink-0 cursor-pointer rounded-xs border border-primary-20 bg-transparent outline-none',
        // Hover border affordance only applies to unchecked tiles — when
        // filled, the green bg owns the surface and a gray hover ring on
        // top reads as a defect.
        'not-data-[state=checked]:not-data-[state=indeterminate]:not-disabled:not-aria-disabled:hover:border-primary-40',
        // Drop the border when filled — `bg-clip: border-box` (default) lets
        // `bg-highlight` paint through the transparent border zone, so the
        // checkbox reads as a uniform tile instead of a tile-with-border.
        // The base `hover:border-primary-40` also stops biting because
        // there's no longer a visible border edge to colour.
        'data-[state=checked]:border-transparent data-[state=checked]:bg-highlight',
        'data-[state=indeterminate]:border-transparent data-[state=indeterminate]:bg-highlight',
        'focus-visible:ring-2 focus-visible:ring-primary-20/50',
        'disabled:cursor-not-allowed disabled:opacity-50',
        sizeStyles[size],
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className="flex h-full w-full items-center justify-center text-white"
      >
        {props.checked === 'indeterminate' ? (
          <Icon name={Icons.Minus} className={indicatorIconSize[size]} />
        ) : (
          <Icon name={Icons.Check} className={indicatorIconSize[size]} />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
};

export type { Props as CheckboxProps };
